import {createContext, useContext, useRef, useState} from "react";
import {IChatMessage, ITherapyChatInfo} from "../util/types";
import {VITE_WEBSOCKET_URL} from "../config";
import {displayNotificationDot, setOnlineUser} from "../util";
import {BoxKeyPair} from "tweetnacl";
import {encrypt, formatChatString, generateKeyPair} from "../util/crypto";
import {fetchTherapistKey, fetchUserAccountRemote} from "../util/UserAccount";
import {useAuth0} from "@auth0/auth0-react";
import {useAuthFetchFromRestAPI} from "../util/api";
import { box } from 'tweetnacl';
import {decodeBase64} from "tweetnacl-util";
import moment from "moment";

type SendMessageParams = {
  text: string;
  therapy: ITherapyChatInfo;
};

type ChatContextType = {
  messages: IChatMessage[];
  initConnection: () => Promise<void>;
  loadMessages: (therapyId: string) => Promise<void>;
  loadMoreMessages: () => Promise<void>;
  sendMessage: (params: SendMessageParams) => Promise<string>;
  resendMessage: (params: SendMessageParams) => Promise<string>;
  isConnected: boolean;
  hasMore: boolean;
  isLoadingMessages: boolean;
  tKeyPair: BoxKeyPair | null;
  patientPublicKey: string | null;
  websocket: WebSocket | null;
  chatHeadMap: Map<string, IChatMessage>;
};

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat must be used within a ChatContext");
  return ctx;
};

export const ChatProvider = ({ children}: { children: React.ReactNode }) => {
  const wsRef = useRef<WebSocket | null>(null);
  const { getIdTokenClaims, getAccessTokenSilently } = useAuth0();
  const { authFetch } = useAuthFetchFromRestAPI();

  const [chatHeadMap, setChatHeadMap] = useState<Map<string, IChatMessage>>(new Map());
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [patientPublicKey, setPatientPublicKey] = useState<string | null>(null);
  const [tKeyPair, setTKeyPair] = useState<BoxKeyPair | null>(null);
  const [page, setPage] = useState(1);
  const [currentTherapyId, setCurrentTherapyId] = useState<string | null>(null);
  const pageSize = 25;

  const updateChatHeadMap = (chatId: string, meta: IChatMessage) => {
    setChatHeadMap(prev => {
      const newMap = new Map(prev);
      newMap.set(chatId, meta);
      return newMap;
    });
  };

  const initConnection = async () => {

    if (wsRef.current) {
      return;
    }

    const token = await getAccessTokenSilently();
    const therapistId = (await fetchUserAccountRemote(authFetch))?._id;
    const idToken = await getIdTokenClaims();

    if (idToken) {
      const keyData = await fetchTherapistKey(authFetch, idToken);
      if (keyData) {
        setTKeyPair(generateKeyPair(keyData.toString()));
      }
    }

    const socket = new WebSocket(VITE_WEBSOCKET_URL + "/t:" + therapistId, token);

    socket.onopen = () => {
      setIsConnected(true);
      localStorage.removeItem('_online_');
    }
    socket.onmessage = (messageEvent) => {
      const message = JSON.parse(messageEvent.data);
      if (message) {
        switch (message.messageType) {
          case 'OFFLINE':
            displayNotificationDot();
            break;
          case 'ACK':
            handleAck(message.ackId);
            break;
          default:
            updateChatHeadMap(message.senderId, message);
            setMessages((prevMessages) => [message, ...prevMessages]);
            setOnlineUser(message.senderId, true);
            break;
        }
      }
    }
    socket.onclose = () => {
      setIsConnected(false);
      wsRef.current = null;
    };

    wsRef.current = socket;
  };

  const handleAck = (ackId: string) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.ackId === ackId ? { ...msg, ackId: undefined } : msg
      )
    );
  };

  const loadMessages = async (therapyId: string) => {
    setIsLoadingMessages(true);
    setMessages([]);
    setPage(1);
    setHasMore(true);
    setCurrentTherapyId(therapyId);

    try {
      const chatHead = await authFetch(`/api/v1/chat/${therapyId}/1/${pageSize}`);
      if (chatHead && chatHead.messages) {
        setPatientPublicKey(chatHead.patientKey);
        setMessages(chatHead.messages);
        setHasMore(chatHead.messages.length === pageSize);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const getMessagesPage = async (pageNo: number) => {
    if (!currentTherapyId) return;

    const chatHead = await authFetch(`/api/v1/chat/${currentTherapyId}/${pageNo}/${pageSize}`);
    if (chatHead && chatHead.messages) {
      setPatientPublicKey(chatHead.patientKey);
      if (chatHead.messages.length > 0) {
        setMessages((prev) => [...prev, ...chatHead.messages]);
      } else {
        setHasMore(false);
      }
    }
  };

  const loadMoreMessages = async () => {
    if (!hasMore || isLoadingMessages) return;

    const nextPage = page + 1;
    setIsLoadingMessages(true);

    try {
      await getMessagesPage(nextPage);
      setPage(nextPage);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const sendMessage = async ({text, therapy}: SendMessageParams) => {
    if(!tKeyPair || !patientPublicKey) {
      return '';
    }

    const ackId = Date.now().toString();
    const finalKeyTherapist = box.before(decodeBase64(patientPublicKey), tKeyPair.secretKey);
    const actualText = formatChatString(text);

    const message: IChatMessage = {
      createdAt: moment().toISOString(),
      messageType: 'TEXT',
      senderId: therapy.therapist,
      text: encrypt(finalKeyTherapist, actualText),
      ackId,
      isNew: true,
    };

    const chat = {
      therapyId: therapy._id,
      connectionId: therapy.patient._id,
      message,
    };

    wsRef.current?.send(JSON.stringify(chat));
    setMessages((prev) => [message, ...prev]);
    updateChatHeadMap(therapy._id, message);

    return ackId;
  };

  const resendMessage = async ({text, therapy}: SendMessageParams) => {
      if (!patientPublicKey || !tKeyPair ) return '';

      const finalKeyTherapist = box.before(
        decodeBase64(patientPublicKey),
        tKeyPair.secretKey
      );

      const actualText = formatChatString(text);

      const ackId = moment().unix().toString();

      const message: IChatMessage = {
        messageType: 'TEXT',
        senderId: therapy.therapist,
        text: encrypt(finalKeyTherapist, actualText),
        ackId,
        isNew: true,
        createdAt: moment().toISOString(),
      };

      const chat = {
        therapyId: therapy._id,
        connectionId: therapy.patient._id,
        message,
      };

      wsRef.current?.send(JSON.stringify(chat));
      setMessages((prev) => [message, ...prev.slice(1)]);

      return ackId;
    };

  return (
    <ChatContext.Provider value={{
      messages,
      initConnection,
      loadMessages,
      loadMoreMessages,
      sendMessage,
      resendMessage,
      isConnected,
      hasMore,
      isLoadingMessages,
      tKeyPair,
      patientPublicKey,
      websocket: wsRef.current,
      chatHeadMap
    }}>
      {children}
    </ChatContext.Provider>
  );
};